// Drop-in replacement for MUI's <Dialog/> that goes full-screen on narrow
// viewports (xs/sm). Use this for any non-trivial dialog so phone users get a
// usable layout instead of a centred 320px card with cramped form fields.
//
// API mirrors MUI's Dialog — pass it the same props. Add `mobileFullScreen={false}`
// to opt out (e.g. small confirmation dialogs that should stay floating).

import React from 'react';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import Slide, { SlideProps } from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { useIsNarrow } from '../../hooks/useBreakpoints';

interface ResponsiveDialogProps extends DialogProps {
  /** When true (default) the dialog goes full-screen on xs viewports. */
  mobileFullScreen?: boolean;
}

const SlideUp = React.forwardRef(function SlideUp(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...(props as SlideProps)} />;
});

const ResponsiveDialog: React.FC<ResponsiveDialogProps> = ({
  mobileFullScreen = true,
  fullScreen,
  TransitionComponent,
  children,
  ...rest
}) => {
  const narrow = useIsNarrow();
  const goFull = mobileFullScreen && narrow;
  return (
    <Dialog
      {...rest}
      fullScreen={fullScreen ?? goFull}
      TransitionComponent={TransitionComponent ?? (goFull ? SlideUp : undefined)}
    >
      {children}
    </Dialog>
  );
};

export default ResponsiveDialog;
